// Dear dev,
// this component is copy from gamma to support some uses cases
// the decision to copy the component is because this changes are transitional
// and later we will be using the factorial one combobox
// use cases
// - tag custom icon
// - tag custom tooltip
// - showing tag of another color when disabled
// - having a create component SelectOrCreate could not be used because we need search and tags
// - loading and custom loading skeleton
// - minumum characters for start debounce and onInputChange
import * as React from 'react'

import { Box, ScrollContainer, SearchField, matchText } from '@factorialco/deprecated-design-system'
import { xor } from 'lodash'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'

import Blank from '../Blank'
import { InternalOption as Option, type PrivateProps as OptionProps } from '../Option'

export interface ContainerProps {
  value: string[]
  headerLabel?: string
  blankLabel: string
  children: Array<React.ReactElement<OptionProps>>
  searchPlaceholder?: string
  onChange: (value: string[] | string | null) => void
  isMultiSelect?: boolean
  hasSearch?: boolean
  defaultSearchPlaceholder: string
  creationComponent?: (search: string) => React.ReactNode
  onDebouncedSearch?: (value: string) => void
  debouncedMinimumCharacters?: number
  loadingSearch?: boolean
  loadingSearchComponent?: () => React.ReactNode
}

const Container: React.FC<ContainerProps> = ({
  value,
  headerLabel,
  blankLabel,
  children,
  searchPlaceholder,
  onChange,
  isMultiSelect,
  hasSearch = true,
  defaultSearchPlaceholder,
  creationComponent,
  onDebouncedSearch,
  debouncedMinimumCharacters = 0,
  loadingSearch,
  loadingSearchComponent,
}) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch('', {
    minimumCharacters: debouncedMinimumCharacters,
  })

  const filteredChildren = useUnsafeMemo(() => {
    if (!children) return []

    return children.filter((x) => matchText(debouncedSearch, x.props.searchValue))
  }, [debouncedSearch, children])
  const enabledFilteredChildren = useUnsafeMemo(
    () => filteredChildren.filter((x) => !x.props.isDisabled),
    [filteredChildren]
  )
  const hasAllValuesSelected = useUnsafeMemo(
    () => enabledFilteredChildren.every((x) => value.includes(x.props.value)),
    [enabledFilteredChildren, value]
  )
  const enabledFilteredChildrenSize = enabledFilteredChildren.length

  const handleChange = (change: string) => {
    let nextValue

    if (isMultiSelect) {
      nextValue = xor(value, [change])
    } else {
      nextValue = change === value[0] ? null : change
    }

    onChange(nextValue)
  }

  const handleToggleAll = () => {
    const nextValues = value.length
      ? []
      : React.Children.map<string, React.ReactElement<OptionProps>>(
          enabledFilteredChildren,
          (x) => x.props.value
        )

    onChange(nextValues)
  }

  const options = React.Children.map<React.ReactNode, React.ReactElement<OptionProps>>(
    filteredChildren,
    (x) => {
      const optionValue = x.props.value
      const isChecked = value.includes(optionValue)

      const onChangeHandle = () => {
        handleChange(optionValue)
      }

      return React.cloneElement(x, {
        onChange: onChangeHandle,
        isChecked,
        isMultiSelect,
      })
    }
  )

  useUnsafeEffect(() => {
    onDebouncedSearch?.(debouncedSearch)
  }, [debouncedSearch, onDebouncedSearch])

  return (
    <Box
      border={{
        all: {
          color: 'grey400',
          width: 's1',
          style: 'solid',
        },
      }}
      borderRadius={{
        all: 'abs008',
      }}
      overflowX="hidden"
    >
      {hasSearch && (
        <Box width="full" padding="s8" background="grey200">
          <SearchField
            variant="big"
            search={search}
            onSearch={onSearch}
            placeholder={searchPlaceholder ?? defaultSearchPlaceholder}
          />
        </Box>
      )}
      <ScrollContainer maxHeight="s300">
        {isMultiSelect && headerLabel && (
          <Option
            label=""
            searchValue=""
            value={''}
            onChange={handleToggleAll}
            isChecked={enabledFilteredChildrenSize === 0 ? false : hasAllValuesSelected}
            isHeader
            isMultiSelect={isMultiSelect}
            isDisabled={enabledFilteredChildrenSize === 0}
          >
            {headerLabel}
          </Option>
        )}
        {creationComponent && creationComponent(search)}
        {!!options.length && options}
        {loadingSearch && loadingSearchComponent?.()}
        {!options.length && !loadingSearch && <Blank>{blankLabel}</Blank>}
      </ScrollContainer>
    </Box>
  )
}

export default Container
